/**
 * Cost Trend Chart Component
 *
 * Stacked area chart showing daily AWS costs by service over time.
 */

import { useId, useMemo } from 'react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { formatCurrency, formatChartDate } from '@/lib/formatters';
import { ChartCard, CHART_HEIGHT_CLASSES } from './ChartCard';
import type { DailyCost } from '@/types/dashboard';

interface CostTrendChartProps {
  data: DailyCost[] | undefined;
  isLoading: boolean;
}

// Color palette for services
const SERVICE_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'hsl(240, 60%, 50%)', // Additional colors if needed
  'hsl(180, 60%, 50%)',
  'hsl(120, 60%, 50%)',
];

// Shorten verbose AWS service names for display
const SERVICE_DISPLAY_NAMES: Record<string, string> = {
  'AWS Lambda': 'Lambda',
  'Amazon DynamoDB': 'DynamoDB',
  'Amazon Simple Storage Service': 'S3',
  'Amazon S3': 'S3',
  'Amazon Bedrock': 'Bedrock',
  'Amazon CloudWatch': 'CloudWatch',
  'Amazon API Gateway': 'API Gateway',
  'AWS Secrets Manager': 'Secrets Manager',
  'Amazon Cognito': 'Cognito',
  'AWS Key Management Service': 'KMS',
  'Amazon CloudFront': 'CloudFront',
};

function getServiceDisplayName(service: string): string {
  return SERVICE_DISPLAY_NAMES[service] || service;
}

export function CostTrendChart({ data, isLoading }: CostTrendChartProps) {
  const chartId = useId();

  // Extract unique services and build chart config
  const { chartConfig, services, chartData } = useMemo(() => {
    if (!data || data.length === 0) {
      return { chartConfig: {} as ChartConfig, services: [], chartData: [] };
    }

    // Collect all services across all days
    const serviceSet = new Set<string>();
    data.forEach((day) => {
      Object.keys(day.byService).forEach((s) => serviceSet.add(s));
    });

    const services = Array.from(serviceSet);

    // Build chart config with colors
    const config: ChartConfig = {};
    services.forEach((service, i) => {
      const displayName = getServiceDisplayName(service);
      config[service] = {
        label: displayName,
        color: SERVICE_COLORS[i % SERVICE_COLORS.length],
      };
    });

    // Transform data for chart (add formatted date and spread services)
    const chartData = data.map((day) => ({
      date: formatChartDate(day.date),
      dateRaw: day.date,
      total: day.cost,
      ...day.byService,
    }));

    return { chartConfig: config, services, chartData };
  }, [data]);

  const showSkeleton = isLoading || data === undefined;
  const isEmpty = !showSkeleton && (!data || data.length === 0);

  return (
    <ChartCard
      title="Daily Cost Trend"
      isLoading={showSkeleton}
      isEmpty={isEmpty}
      emptyMessage="No cost data available"
    >
      <ChartContainer config={chartConfig} className={CHART_HEIGHT_CLASSES}>
        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
        >
          <defs>
            {services.map((service, i) => (
              <linearGradient
                key={service}
                id={`${chartId}-gradient-${i}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="5%"
                  stopColor={SERVICE_COLORS[i % SERVICE_COLORS.length]}
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor={SERVICE_COLORS[i % SERVICE_COLORS.length]}
                  stopOpacity={0.1}
                />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            minTickGap={32}
            className="text-xs"
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={(v) => formatCurrency(v, true)}
            className="text-xs"
            width={60}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(value) => formatCurrency(Number(value))}
              />
            }
          />
          <ChartLegend content={<ChartLegendContent />} />
          {services.map((service, i) => (
            <Area
              key={service}
              dataKey={service}
              stackId="1"
              type="monotone"
              fill={`url(#${chartId}-gradient-${i})`}
              stroke={SERVICE_COLORS[i % SERVICE_COLORS.length]}
              strokeWidth={2}
            />
          ))}
        </AreaChart>
      </ChartContainer>
    </ChartCard>
  );
}
